import { useEffect, useRef, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import _ from "lodash";
import { useDispatch } from "react-redux";
import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import { handleError, getAssignedTechsNameArray, isAdmin } from "src/Common/function";
import LoadMoreButton from "src/Components/Common/loadmorebutton";
import Loader from "src/Components/Common/Loader";
import WOStatusComponent from "../WO/Components/Status/wostatuscomponent";
import BranchBar from "src/Components/branchbar/BranchBar";
import WoType from "src/Pages/WO/Components/Types/WoType";
import Highlight from "src/Components/Common/Highlight";
import { setWONumber } from "src/Redux/Slices/HeaderSlice";
import { getUploadUrl } from "src/Common/common";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";

const REC_SCROLL_STORAGE_KEY = "REC_SCROLL_RESTORE";

// Each recommendation on its own line with what the customer did with it:
// Pending (nothing yet), a request made from it (click to open that work
// order) or "Not Now".
function RecommendationItems({ items, keyword, onOpenWo }) {
  // The photo being viewed: { photos, index }.
  const [viewing, setViewing] = useState(null);
  return (
    <div className="flex flex-col gap-2">
      {/* Inside a clickable row: keep the viewer's clicks from opening the WO. */}
      {viewing && (
        <div onClick={(event) => event.stopPropagation()}>
          <AttachmentImageSlider
            shown
            attachments={viewing.photos.map((name, index) => ({
              id: index,
              fileName: name,
              displayName: `Photo ${index + 1}`,
            }))}
            activeIndex={viewing.index}
            onCancel={() => setViewing(null)}
          />
        </div>
      )}
      {items.map((item, index) => (
        <div key={item.id} className="flex items-start gap-2">
          {items.length > 1 && (
            <span className="font-OpenSansSemiBold text-[12px] leading-[20px] text-[#0065FD]">{index + 1}.</span>
          )}
          <div className="flex-1 min-w-0">
            <span className="whitespace-pre-line">
              <Highlight text={item.text} keyword={keyword} />
            </span>
            {item.photos?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {item.photos.map((name, index) => (
                  <button
                    key={name}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setViewing({ photos: item.photos, index });
                    }}
                    className="cursor-pointer"
                  >
                    <img
                      src={getUploadUrl(name)}
                      alt=""
                      className="w-10 h-10 rounded object-cover bg-[#E5E8F3] border border-[#E5E8F3]"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          {item.status === 1 && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                if (item.requestedWo) onOpenWo(item.requestedWo);
              }}
              className="shrink-0 px-2 py-0.5 rounded-md bg-[#EFF6FF] text-[#0065FD] font-OpenSansSemiBold text-[12px] leading-[18px] cursor-pointer hover:underline"
              title="Open the customer's request"
            >
              {`${item.requestType === 1 ? "Quote" : "Service"} requested${
                item.requestedWo?.number ? ` · ${item.requestedWo.number}` : ""
              }`}
            </button>
          )}
          {item.status === 0 && (
            <span className="shrink-0 px-2 py-0.5 rounded-md bg-[#FEF9C3] text-[#A16207] font-OpenSansSemiBold text-[12px] leading-[18px]">
              Pending
            </span>
          )}
          {item.status === 2 && (
            <span className="shrink-0 px-2 py-0.5 rounded-md bg-[#F1F5F9] text-[#5F6477] font-OpenSansSemiBold text-[12px] leading-[18px]">
              Not Now
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function Recommendations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const branch = Number(searchParams.get("branch"));

  // const keyword = useSelector((state) => state.Keyword.keyword);
  const [orderArray, setOrderArray] = useState([]);
  const [loadMoreButtonShown, setLoadMoreButtonShown] = useState(false);
  // True while a scroll restore is fetching the previously loaded rows.
  const [restoring, setRestoring] = useState(false);

  const keyword = searchParams.get("keyword") || "";

  // Pending scroll restoration coming back from the WO detail page.
  const restoreRef = useRef(null);
  // Bumped on every fresh fetch cycle; responses from an older cycle (e.g.
  // the double fetch caused by BranchBar's mount-time navigation) are dropped.
  const fetchGenRef = useRef(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    const saved = sessionStorage.getItem(REC_SCROLL_STORAGE_KEY);
    let pending = null;
    try {
      pending = saved ? JSON.parse(saved) : null;
      if (pending && pending.search !== undefined && pending.search !== location.search) {
        // Saved from a different filter/branch context — not restorable here.
        sessionStorage.removeItem(REC_SCROLL_STORAGE_KEY);
        pending = null;
      }
    } catch {
      pending = null;
      sessionStorage.removeItem(REC_SCROLL_STORAGE_KEY);
    }
    restoreRef.current = pending;
    setRestoring(!!pending);
    // A pending restore fetches every previously loaded row in one request.
    const limit = pending?.loadedCount > 0 ? pending.loadedCount : undefined;
    getAllRecommendations([], { limit }, gen);
  }, [branch, keyword]);

  // Restore the exact scrollTop saved at click time once the saved WO renders.
  useEffect(() => {
    const saved = sessionStorage.getItem(REC_SCROLL_STORAGE_KEY);
    if (!saved) return;
    try {
      const { id, scrollTop } = JSON.parse(saved);
      if (orderArray.some((order) => order.id === id)) {
        sessionStorage.removeItem(REC_SCROLL_STORAGE_KEY);
        setTimeout(() => {
          const container = scrollContainerRef.current;
          if (container && typeof scrollTop === "number") {
            container.scrollTop = scrollTop;
          } else {
            document.getElementById(`rec-row-${id}`)?.scrollIntoView({ block: "center", inline: "nearest" });
          }
        }, 0);
      }
    } catch {
      sessionStorage.removeItem(REC_SCROLL_STORAGE_KEY);
    }
  }, [orderArray]);

  const getAllRecommendations = (previousOrderArray, { limit } = {}, gen = fetchGenRef.current) => {
    // Offset-based continuation: rows deleted from the list can't shift page
    // boundaries the way pageNumber-based pagination could.
    const offset = previousOrderArray.length;
    WOService.getAllRecommendations(keyword, 0, branch, limit, offset)
      .then((response) => {
        if (gen !== fetchGenRef.current) return;
        console.log("AAAAAAAA: ", response.data, "OrderArray: ", orderArray);
        if (response.status == 200) {
          let temp = [...previousOrderArray, ...response.data.orders];
          for (let i = 0; i < temp.length; i++) {
            temp[i].techNames = getAssignedTechsNameArray(temp[i].assignedTechs);
          }
          setOrderArray(temp);
          setLoadMoreButtonShown(temp.length < response.data.totalCount);

          // Scroll restore is single-shot: the id is either in this response
          // or the saved entry is stale. The effect above does the scroll.
          if (restoreRef.current) {
            const found = temp.some((order) => order.id === restoreRef.current.id);
            restoreRef.current = null;
            if (!found) {
              sessionStorage.removeItem(REC_SCROLL_STORAGE_KEY);
            }
            setRestoring(false);
          }
        }
      })
      .catch((error) => {
        console.log("error: ", error);
        setRestoring(false);
        handleError(error);
      });
  };

  const handleLoadMore = () => {
    getAllRecommendations(orderArray);
  };

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColor">
      <div className=" flex-1 min-h-0 flex flex-col">
        <div className="px-5 py-2 border-b border-borderColor">
          <BranchBar />
        </div>
        <div className="p-5 flex-1 min-h-0 flex flex-col">
          <div
            className="relative w-full flex-1 min-h-0 flex flex-col overflow-hidden"
            style={{
              borderRadius: "12px",
              border: "1px solid #E5E8F3",
              background: "#FFF",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            }}
          >
            {restoring && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
                <Loader variant="block" />
              </div>
            )}
            <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[1250px]">
                <thead className="sticky top-0 z-10 bg-[#F8F9FE]">
                  <tr
                    style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}
                    className="[&>th]:border-r [&>th]:border-borderColor [&>th:last-child]:border-r-0"
                  >
                    <th>
                      <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center pl-4 w-[100px] h-[44px] text-[#5F6477] text-center">
                        <span>{vocabularize("WO#")}</span>
                      </div>
                    </th>
                    <th>
                      <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[120px] h-[44px] text-[#5F6477]">
                        Type
                      </div>
                    </th>
                    <th>
                      <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[230px] h-[44px] text-[#5F6477]">
                        Customer
                      </div>
                    </th>
                    <th className="w-full">
                      <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 h-[44px] text-[#5F6477]">
                        {vocabularize("Recommendations")}
                      </div>
                    </th>
                    <th>
                      <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[250px] h-[44px] text-[#5F6477]">
                        Technician
                      </div>
                    </th>
                    <th>
                      <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[130px] h-[44px] text-[#5F6477]">
                        Status
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orderArray.map((order, index) => (
                    <tr
                      key={index}
                      id={`rec-row-${order.id}`}
                      onClick={(event) => {
                        if (event.ctrlKey || event.metaKey) {
                          window.open("/work/" + String(order.id), "_blank");
                          return;
                        }
                        sessionStorage.setItem(
                          REC_SCROLL_STORAGE_KEY,
                          JSON.stringify({
                            id: order.id,
                            offset: index,
                            loadedCount: orderArray.length,
                            scrollTop: scrollContainerRef.current?.scrollTop ?? 0,
                            search: window.location.search,
                          })
                        );
                        dispatch(setWONumber(String(order.number)));
                        navigate("/work/" + String(order.id));
                      }}
                      className="border-b border-borderColor cursor-pointer hover:bg-[#FAFAFB] [&>td]:border-r [&>td]:border-borderColor [&>td:last-child]:border-r-0"
                    >
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-center text-[13px] leading-[20px] font-[400] text-[#1A2332] col-span-1">
                          <Highlight text={order.number} keyword={keyword} />
                        </div>
                      </td>
                      <td>
                        <div className="mx-auto flex justify-center">
                          <WoType serviceType={order.serviceType} />
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                          <Highlight text={order.customer?.companyName} keyword={keyword} />
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] text-[#1A2332] text-ellipsis word-break-break-word">
                          {order.recommendationItems?.length > 0 ? (
                            <RecommendationItems
                              items={order.recommendationItems}
                              keyword={keyword}
                              onOpenWo={(wo) => {
                                dispatch(setWONumber(String(wo.number)));
                                navigate("/work/" + String(wo.id));
                              }}
                            />
                          ) : (
                            <Highlight text={order.recommendations} keyword={keyword} />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] text-[#1A2332] text-ellipsis word-break-break-word">
                          <Highlight text={order.techNames} keyword={keyword} />
                        </div>
                      </td>
                      <td>
                        <div className="px-4 py-3 flex items-center gap-3">
                          <WOStatusComponent value={order.status} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loadMoreButtonShown && (
                <div className="w-full py-[10px] flex justify-center bg-white">
                  <LoadMoreButton onClicked={handleLoadMore} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Recommendations;
